import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Search, Building2, Check, X } from "lucide-react";
import CONFIG from "@/lib/config";
import { apiRequest } from "@/lib/api";

interface AddUserFromACCDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onUserAdded?: () => void;
  existingUsers?: {
    superUser: any;
    hubAdmins: any[];
    projectUsers: any[];
  };
}

export function AddUserFromACCDialog({ 
  isOpen, 
  onClose, 
  projectId, 
  onUserAdded,
  existingUsers = { superUser: null, hubAdmins: [], projectUsers: [] }
}: AddUserFromACCDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [accUserSearchTerm, setAccUserSearchTerm] = useState("");
  const [selectedAccUsers, setSelectedAccUsers] = useState<any[]>([]);
  const [newUserPermission, setNewUserPermission] = useState("project_admin");

  // Get ACC users for the current project
  const { data: accUsers = [], isLoading: accUsersLoading } = useQuery<any[]>({
    queryKey: ["project-users", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      return await apiRequest(`/api/autodesk/projects/${projectId}/users?source=acc`);
    },
    enabled: !!projectId && isOpen,
  });

  const addUserMutation = useMutation({
    mutationFn: async (userData: {
      email: string;
      firstName: string;
      lastName: string;
      permissionLevel: string;
      accUserId?: string;
      accProjectId?: string;
      accHubId?: string;
      accAccountId?: string;
    }) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/users/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userData),
      });
      if (!response.ok) throw new Error('Failed to add user');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      onClose();
      setSelectedAccUsers([]);
      setAccUserSearchTerm("");
      setNewUserPermission("project_admin");
      if (onUserAdded) {
        onUserAdded();
      }
      toast({
        title: "Success",
        description: "Users added successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add user.",
        variant: "destructive",
      });
    },
  });

  // Handle ACC user selection with tick box
  const handleAccUserToggle = (accUser: any) => {
    setSelectedAccUsers(prev => {
      const isSelected = prev.some(user => user.autodeskId === accUser.autodeskId);
      if (isSelected) {
        return prev.filter(user => user.autodeskId !== accUser.autodeskId);
      } else {
        return [...prev, accUser];
      }
    });
  };

  const isUserSelected = (accUser: any) => {
    return selectedAccUsers.some(user => user.autodeskId === accUser.autodeskId);
  };

  // Check if user is already added to the system
  const isUserAlreadyAdded = (accUser: any) => {
    const userEmail = accUser.email?.toLowerCase();
    if (!userEmail) return false;
    
    // Check in super user
    const isSuperUser = existingUsers.superUser && 
      existingUsers.superUser.email?.toLowerCase() === userEmail;
    
    // Check in hub admins
    const isHubAdmin = existingUsers.hubAdmins.some(user => 
      user.email?.toLowerCase() === userEmail
    );
    
    // Check in project users
    const isProjectUser = existingUsers.projectUsers.some(user => 
      user.email?.toLowerCase() === userEmail
    );
    
    return isSuperUser || isHubAdmin || isProjectUser;
  };

  // Get user's current role if already added
  const getUserCurrentRole = (accUser: any) => {
    const userEmail = accUser.email?.toLowerCase();
    if (!userEmail) return null;
    
    // Check in super user first
    if (existingUsers.superUser && 
        existingUsers.superUser.email?.toLowerCase() === userEmail) {
      return {
        type: 'super_user',
        level: existingUsers.superUser.permissionLevel,
        displayName: 'Super User'
      };
    }
    
    // Check in hub admins
    const hubAdmin = existingUsers.hubAdmins.find(user => 
      user.email?.toLowerCase() === userEmail
    );
    if (hubAdmin) {
      return {
        type: 'hub_admin',
        level: hubAdmin.permissionLevel,
        displayName: hubAdmin.permissionLevel === 'hub_super_user' ? 'Hub Super User' : 'Hub Admin'
      };
    }
    
    // Check in project users
    const projectUser = existingUsers.projectUsers.find(user => 
      user.email?.toLowerCase() === userEmail
    );
    if (projectUser) {
      return {
        type: 'project_user',
        level: projectUser.permissionLevel,
        displayName: projectUser.permissionLevel === 'project_admin' ? 'Project Admin' : 
                   projectUser.permissionLevel === 'tender_user' ? 'Tender User' : 
                   projectUser.permissionLevel || 'Unknown'
      };
    }
    
    return null;
  };

  // Filter ACC users based on search term
  const filteredAccUsers = accUsers.filter((user: any) => {
    if (!accUserSearchTerm) return true;
    const searchLower = accUserSearchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.companyName?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddUsers = () => {
    if (selectedAccUsers.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one ACC user to add.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('🔍 [Frontend] Adding users with permission:', newUserPermission);
    
    // Add each selected user
    selectedAccUsers.forEach(accUser => {
      const userData = {
        email: accUser.email,
        firstName: accUser.firstName,
        lastName: accUser.lastName,
        permissionLevel: newUserPermission,
        accUserId: accUser.autodeskId, // ACC user ID from Autodesk
        accProjectId: projectId, // Current project ID
        accHubId: null, // Will be set by backend from project settings
        accAccountId: null, // Will be set by backend from project settings
      };
      
      console.log('🔍 [Frontend] Sending user data:', userData);
      addUserMutation.mutate(userData);
    });
  };

  const handleClose = () => {
    onClose();
    setSelectedAccUsers([]);
    setAccUserSearchTerm("");
    setNewUserPermission("project_admin");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add New User from ACC</DialogTitle>
          <DialogDescription>
            Select an existing ACC user to add to the procurement system.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Search ACC Users */}
          <div className="space-y-2">
            <Label htmlFor="search">Search ACC Users</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                id="search"
                placeholder="Search by name, email, or company..."
                value={accUserSearchTerm}
                onChange={(e) => setAccUserSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* ACC Users List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {accUsersLoading ? (
              <div className="p-4 text-center text-gray-500">
                Loading ACC users...
              </div>
            ) : filteredAccUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {accUserSearchTerm ? 'No users found matching your search.' : 'No ACC users available.'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredAccUsers.map((user: any) => {
                  const isAlreadyAdded = isUserAlreadyAdded(user);
                  const currentRole = getUserCurrentRole(user);
                  const isSelected = isUserSelected(user);
                  
                  return (
                    <div
                      key={user.autodeskId}
                      className={`p-4 transition-colors ${
                        isAlreadyAdded 
                          ? 'bg-gray-50 border-l-4 border-gray-300 cursor-not-allowed opacity-75' 
                          : isSelected 
                            ? 'bg-blue-50 border-l-4 border-blue-500 cursor-pointer hover:bg-blue-100' 
                            : 'cursor-pointer hover:bg-gray-50'
                      }`}
                      onClick={(e) => {
                        // Don't trigger if user is already added or clicking on the checkbox itself
                        if (isAlreadyAdded || (e.target as HTMLInputElement).type === 'checkbox') {
                          return;
                        }
                        handleAccUserToggle(user);
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isAlreadyAdded}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (!isAlreadyAdded) {
                                handleAccUserToggle(user);
                              }
                            }}
                            className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                              isAlreadyAdded ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          />
                        </div>
                        <div className="flex-shrink-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isAlreadyAdded ? 'bg-gray-300' : 'bg-gray-200'
                          }`}>
                            <span className={`text-sm font-medium ${
                              isAlreadyAdded ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className={`text-sm font-medium truncate ${
                              isAlreadyAdded ? 'text-gray-500' : 'text-gray-900'
                            }`}>
                              {user.firstName} {user.lastName}
                            </p>
                            {isSelected && !isAlreadyAdded && (
                              <Check className="h-4 w-4 text-blue-500" />
                            )}
                            {isAlreadyAdded && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-xs text-gray-500">Already Added</span>
                              </div>
                            )}
                          </div>
                          <p className={`text-sm truncate ${
                            isAlreadyAdded ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {user.email}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Building2 className={`h-3 w-3 ${
                              isAlreadyAdded ? 'text-gray-300' : 'text-gray-400'
                            }`} />
                            <p className={`text-xs truncate ${
                              isAlreadyAdded ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {user.companyName}
                            </p>
                            {currentRole && (
                              <div className="ml-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  currentRole.type === 'super_user'
                                    ? 'bg-red-100 text-red-700 border border-red-200'
                                    : currentRole.type === 'hub_admin' 
                                      ? currentRole.level === 'hub_super_user'
                                        ? 'bg-red-100 text-red-700 border border-red-200'
                                        : 'bg-purple-100 text-purple-700 border border-purple-200'
                                      : currentRole.level === 'project_admin'
                                        ? 'bg-green-100 text-green-700 border border-green-200'
                                        : currentRole.level === 'tender_user'
                                          ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                          : 'bg-blue-100 text-blue-700 border border-blue-200'
                                }`}>
                                  {currentRole.displayName}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Users Summary */}
          {selectedAccUsers.length > 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-2">
                Selected Users ({selectedAccUsers.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedAccUsers.map((user: any) => (
                  <div key={user.autodeskId} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">
                      {user.firstName} {user.lastName} ({user.email})
                    </span>
                    <button
                      onClick={() => handleAccUserToggle(user)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Label htmlFor="permission">Permission Level for All Selected Users</Label>
                <Select value={newUserPermission} onValueChange={(value) => {
                  console.log('🔍 [Frontend] Permission changed to:', value);
                  setNewUserPermission(value);
                }}>
                  <SelectTrigger 
                    onClick={(e) => {
                      console.log('🔍 [Frontend] SelectTrigger clicked');
                      e.stopPropagation();
                    }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10010]">
                    <SelectItem value="hub_super_user">Hub Super User</SelectItem>
                    <SelectItem value="hub_admin">Hub Admin</SelectItem>
                    <SelectItem value="project_admin">Project Admin</SelectItem>
                    <SelectItem value="tender_user">Tender User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddUsers}
            disabled={addUserMutation.isPending || selectedAccUsers.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {addUserMutation.isPending ? "Adding..." : `Add ${selectedAccUsers.length} User${selectedAccUsers.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}